import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  EmailPreviewLead,
  useAuthorizeSendingMutation,
  useGetEmailPreviewQuery,
  useUpdateEmailSequencesMutation,
  useUpdateLeadDataMutation,
} from '@api/scraperApi';
import styled, { keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// SHORTCODE REPLACEMENT
// ═══════════════════════════════════════════════════════════════

const SHORTCODE_REGEX = /\{\{(\w+)\}\}/g;

// Sender-related shortcodes are not lead data — exclude from "missing fields" detection
const SENDER_SHORTCODES = new Set(['sender_name', 'sendername', 'my_name', 'myname']);

const AVAILABLE_SHORTCODES = [
  { key: 'firstName', label: 'First Name', aliases: ['first_name', 'firstname'] },
  { key: 'lastName', label: 'Last Name', aliases: ['last_name', 'lastname'] },
  { key: 'name', label: 'Full Name', aliases: ['full_name', 'fullname'] },
  { key: 'company', label: 'Company', aliases: ['company_name', 'companyname'] },
  { key: 'email', label: 'Email', aliases: [] },
  { key: 'phone', label: 'Phone', aliases: [] },
  { key: 'city', label: 'City', aliases: [] },
  { key: 'state', label: 'State', aliases: [] },
  { key: 'industry', label: 'Industry', aliases: [] },
];

function replaceShortcodes(
  template: string,
  lead: EmailPreviewLead,
  senderName?: string,
): { text: string; unresolved: string[] } {
  const unresolved: string[] = [];
  const firstName = lead.firstName || '';
  const lastName = lead.lastName || '';
  const company = lead.company || '';
  const city = lead.city || '';
  const state = lead.state || '';
  const industry = lead.industry || '';

  const map: Record<string, string> = {
    first_name: firstName,
    firstname: firstName,
    last_name: lastName,
    lastname: lastName,
    name: `${firstName} ${lastName}`.trim() || company,
    full_name: `${firstName} ${lastName}`.trim() || company,
    fullname: `${firstName} ${lastName}`.trim() || company,
    company_name: company,
    companyname: company,
    company,
    email: lead.email || '',
    phone: lead.phone || '',
    city,
    state,
    industry,
    sender_name: senderName || '',
    sendername: senderName || '',
  };

  const text = template.replace(SHORTCODE_REGEX, (match, key) => {
    const normalizedKey = key.toLowerCase();
    const value = map[normalizedKey];
    if (value === undefined || value === '') {
      unresolved.push(key);
      return match;
    }
    return value;
  });

  return { text, unresolved };
}

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(0, 200, 224, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(0, 200, 224, 0); }
`;

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const Panel = styled.div`
  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-lg);
  animation: ${fadeIn} 0.3s ease;
  overflow: hidden;
`;

const PanelHeader = styled.div<{ $collapsed?: boolean }>`
  background: linear-gradient(135deg, rgba(0, 200, 224, 0.06) 0%, rgba(0, 255, 127, 0.04) 100%);
  border-bottom: ${p => (p.$collapsed ? 'none' : '1px solid var(--sk-surface-border)')};
  padding: var(--sk-space-5);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(0, 200, 224, 0.1) 0%, rgba(0, 255, 127, 0.07) 100%);
  }
`;

const CollapseToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: 6px 14px;
  border-radius: var(--sk-radius-default);
  border: 1px solid var(--sk-surface-border);
  background: var(--sk-bg-tertiary);
  color: var(--sk-text-secondary);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: var(--sk-surface-hover, rgba(255, 255, 255, 0.08));
    color: var(--sk-text-primary);
    border-color: var(--sk-cyan-500);
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sk-space-4);
  margin-bottom: var(--sk-space-3);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h3`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-lg);
  font-weight: 600;
  color: var(--sk-text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--sk-space-2);

  svg {
    color: var(--sk-cyan-500);
    width: 20px;
    height: 20px;
  }
`;

const HeaderDesc = styled.p`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: var(--sk-space-4);
  flex-wrap: wrap;
  margin-top: var(--sk-space-3);
`;

const Stat = styled.div<{ $variant?: 'cyan' | 'green' | 'amber' | 'red' }>`
  display: flex;
  align-items: baseline;
  gap: var(--sk-space-1);
  font-size: var(--sk-text-sm);

  strong {
    font-family: var(--sk-font-mono);
    font-weight: 700;
    color: ${p =>
      p.$variant === 'cyan'
        ? 'var(--sk-cyan-500)'
        : p.$variant === 'green'
          ? 'var(--sk-green-500)'
          : p.$variant === 'amber'
            ? '#ff9800'
            : p.$variant === 'red'
              ? '#f44336'
              : 'var(--sk-text-primary)'};
  }

  span {
    color: var(--sk-text-tertiary);
  }
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
`;

const AuthBtn = styled.button<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: 8px 20px;
  border-radius: var(--sk-radius-default);
  border: none;
  font-size: var(--sk-text-sm);
  font-weight: 600;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  background: ${p => (p.disabled ? 'var(--sk-bg-tertiary)' : 'var(--sk-cyan-500)')};
  color: ${p => (p.disabled ? 'var(--sk-text-tertiary)' : '#000')};
  transition: all 0.2s;
  animation: ${p => (p.disabled ? 'none' : pulse)} 2s ease infinite;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SelectAllBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-2);
  padding: 6px 14px;
  border-radius: var(--sk-radius-default);
  border: 1px solid var(--sk-surface-border);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  cursor: pointer;
  background: var(--sk-bg-secondary);
  color: var(--sk-text-secondary);
  transition: all 0.2s;

  &:hover {
    border-color: var(--sk-cyan-500);
    color: var(--sk-cyan-500);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 10px 14px;
  text-align: left;
  font-size: var(--sk-text-xs);
  font-weight: 600;
  color: var(--sk-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--sk-surface-border);
  background: var(--sk-bg-secondary);
  white-space: nowrap;
`;

const Tr = styled.tr<{ $selected?: boolean; $hasIssues?: boolean }>`
  cursor: pointer;
  transition: background 0.15s;
  background: ${p =>
    p.$selected ? 'rgba(0, 200, 224, 0.06)' : p.$hasIssues ? 'rgba(255, 152, 0, 0.04)' : 'transparent'};

  &:hover {
    background: rgba(0, 200, 224, 0.08);
  }

  td {
    border-bottom: 1px solid var(--sk-surface-border);
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: var(--sk-text-sm);
  color: var(--sk-text-primary);
  vertical-align: middle;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  accent-color: var(--sk-cyan-500);
  cursor: pointer;
`;

const UnresolvedTag = styled.span`
  display: inline;
  background: rgba(255, 152, 0, 0.2);
  border: 1px solid rgba(255, 152, 0, 0.4);
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 11px;
  font-family: var(--sk-font-mono);
  color: #ff9800;
`;

const IssuesBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
`;

const OkBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(0, 200, 224, 0.15);
  color: var(--sk-cyan-500);
`;

const ExpandedRow = styled.tr`
  td {
    border-bottom: 1px solid var(--sk-surface-border);
  }
`;

const ExpandedContent = styled.div`
  padding: var(--sk-space-3) var(--sk-space-4);
  background: var(--sk-bg-secondary);
`;

const SequenceCard = styled.div`
  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-default);
  padding: var(--sk-space-3) var(--sk-space-4);
  margin-bottom: var(--sk-space-3);

  &:last-child {
    margin-bottom: 0;
  }
`;

const SequenceLabel = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-2);
  margin-bottom: var(--sk-space-2);
  font-size: var(--sk-text-xs);
  font-weight: 600;
  color: var(--sk-text-tertiary);
  text-transform: uppercase;
`;

const SequenceSubject = styled.div`
  font-size: var(--sk-text-sm);
  font-weight: 600;
  color: var(--sk-text-primary);
  margin-bottom: var(--sk-space-2);
`;

const SequenceBody = styled.div`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
  line-height: 1.6;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
`;

const EditField = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-2);
  margin-bottom: var(--sk-space-2);
`;

const EditLabel = styled.label`
  font-size: var(--sk-text-xs);
  color: var(--sk-text-tertiary);
  min-width: 80px;
  text-transform: uppercase;
`;

const EditInput = styled.input`
  flex: 1;
  padding: 4px 8px;
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-sm);
  font-size: var(--sk-text-sm);
  background: var(--sk-bg-primary);
  color: var(--sk-text-primary);

  &:focus {
    outline: none;
    border-color: var(--sk-cyan-500);
  }
`;

const EditTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 8px;
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-sm);
  font-size: var(--sk-text-sm);
  font-family: inherit;
  background: var(--sk-bg-primary);
  color: var(--sk-text-primary);
  resize: vertical;
  line-height: 1.6;

  &:focus {
    outline: none;
    border-color: var(--sk-cyan-500);
  }
`;

const SaveBtn = styled.button`
  padding: 4px 12px;
  border-radius: var(--sk-radius-sm);
  border: 1px solid var(--sk-cyan-500);
  background: rgba(0, 200, 224, 0.1);
  color: var(--sk-cyan-500);
  font-size: var(--sk-text-xs);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(0, 200, 224, 0.2);
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sk-space-3);
  padding: var(--sk-space-3);
  border-top: 1px solid var(--sk-surface-border);
`;

const PageBtn = styled.button<{ disabled?: boolean }>`
  padding: 6px 14px;
  border-radius: var(--sk-radius-sm);
  border: 1px solid var(--sk-surface-border);
  background: var(--sk-bg-secondary);
  color: ${p => (p.disabled ? 'var(--sk-text-tertiary)' : 'var(--sk-text-primary)')};
  font-size: var(--sk-text-sm);
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};

  &:hover:not(:disabled) {
    border-color: var(--sk-cyan-500);
  }
`;

const SuccessBanner = styled.div`
  background: rgba(0, 255, 127, 0.08);
  border: 1px solid rgba(0, 255, 127, 0.3);
  border-radius: var(--sk-radius-default);
  padding: var(--sk-space-4);
  margin: var(--sk-space-4);
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  color: var(--sk-green-500);
  font-weight: 600;
  font-size: var(--sk-text-sm);

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const EmptyState = styled.div`
  padding: var(--sk-space-8);
  text-align: center;
  color: var(--sk-text-tertiary);
  font-size: var(--sk-text-sm);
`;

const ShortcodeChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-family: var(--sk-font-mono);
  cursor: pointer;
  border: 1px solid ${p => (p.$active ? 'var(--sk-cyan-500)' : 'var(--sk-surface-border)')};
  background: ${p => (p.$active ? 'rgba(0, 200, 224, 0.15)' : 'var(--sk-bg-secondary)')};
  color: ${p => (p.$active ? 'var(--sk-cyan-500)' : 'var(--sk-text-secondary)')};
  transition: all 0.15s;

  &:hover {
    border-color: var(--sk-cyan-500);
    color: var(--sk-cyan-500);
  }
`;

const SequenceEditSection = styled.div`
  border-bottom: 1px solid var(--sk-surface-border);
  padding: var(--sk-space-4) var(--sk-space-5);
  background: var(--sk-bg-secondary);
`;

const SequenceEditHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sk-space-3);
`;

// ═══════════════════════════════════════════════════════════════
// HELPER: Render text with highlighted unresolved shortcodes
// ═══════════════════════════════════════════════════════════════

function renderWithHighlights(text: string): React.ReactNode {
  const parts = text.split(SHORTCODE_REGEX);
  if (parts.length === 1) return text;

  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      nodes.push(parts[i]);
    } else {
      nodes.push(<UnresolvedTag key={i}>{`{{${parts[i]}}}`}</UnresolvedTag>);
    }
  }
  return <>{nodes}</>;
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

interface EmailReviewPanelProps {
  campaignId: string;
}

const EmailReviewPanel: FC<EmailReviewPanelProps> = ({ campaignId }) => {
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingLead, setEditingLead] = useState<Record<string, string> | null>(null);
  const [sendingComplete, setSendingComplete] = useState(false);
  const [scheduledCount, setScheduledCount] = useState(0);
  const [editingSequences, setEditingSequences] = useState(false);
  const [sequenceDrafts, setSequenceDrafts] = useState<
    Array<{ subject: string; body: string; delay_days: number; step_number: number }>
  >([]);
  // Per-lead sequence overrides: leadId -> { seqIndex -> { subject, body } }
  const [leadSeqEdits, setLeadSeqEdits] = useState<Record<string, Record<number, { subject: string; body: string }>>>(
    {},
  );
  const [editingLeadSeq, setEditingLeadSeq] = useState<string | null>(null);
  // Track last focused input/textarea element for cursor-position shortcode insertion
  const lastFocusedRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const { data, isLoading, refetch } = useGetEmailPreviewQuery(
    { campaignId, page, pageSize: 25 },
    { skip: !campaignId },
  );

  const [updateLead] = useUpdateLeadDataMutation();
  const [authorizeSending, { isLoading: isAuthorizing }] = useAuthorizeSendingMutation();
  const [updateSequences, { isLoading: isSavingSequences }] = useUpdateEmailSequencesMutation();

  const sequences = useMemo(() => data?.sequences || [], [data?.sequences]);
  const leads = useMemo(() => data?.leads || [], [data?.leads]);
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 25);

  // Compute previews for visible leads
  const leadPreviews = useMemo(() => {
    if (!sequences.length || !leads.length) return [];
    const firstSeq = sequences[0];
    return leads.map(lead => {
      const subjectResult = replaceShortcodes(firstSeq.subject, lead);
      const bodyResult = replaceShortcodes(firstSeq.body, lead);
      // Exclude sender-related shortcodes from "missing fields" detection
      const allUnresolved = [...new Set([...subjectResult.unresolved, ...bodyResult.unresolved])].filter(
        u => !SENDER_SHORTCODES.has(u.toLowerCase()),
      );
      return {
        lead,
        subjectPreview: subjectResult.text,
        bodyPreview: stripHtml(bodyResult.text).slice(0, 120),
        unresolved: allUnresolved,
        hasIssues: allUnresolved.length > 0,
        hasValidEmail: !!lead.email && !lead.email.startsWith('biz-') && !lead.email.endsWith('@leads.local'),
      };
    });
  }, [sequences, leads]);

  // Global stats from backend (for header); fall back to page-level
  const leadsWithIssues =
    data?.stats?.missing_fields ?? leadPreviews.filter(lp => lp.hasIssues && lp.hasValidEmail).length;
  const leadsReady = data?.stats?.ready ?? leadPreviews.filter(lp => lp.hasValidEmail && !lp.hasIssues).length;
  const leadsNoEmail = data?.stats?.no_email ?? leadPreviews.filter(lp => !lp.hasValidEmail).length;

  // All valid lead IDs from backend (across all pages)
  const allValidLeadIds: string[] = data?.valid_lead_ids ?? [];

  // Selection
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(allValidLeadIds));
  }, [allValidLeadIds]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Expand row
  const toggleExpand = useCallback((id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
    setEditingLead(null);
  }, []);

  // Inline edit
  const startEditing = useCallback((lead: EmailPreviewLead) => {
    setEditingLead({
      id: lead.id,
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      company: lead.company || '',
      city: lead.city || '',
      state: lead.state || '',
      industry: lead.industry || '',
    });
  }, []);

  const handleEditChange = useCallback((field: string, value: string) => {
    setEditingLead(prev => (prev ? { ...prev, [field]: value } : null));
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingLead) return;
    const { id, ...fields } = editingLead;
    await updateLead({ campaignId, leadId: id, data: fields });
    setEditingLead(null);
    refetch();
  }, [editingLead, campaignId, updateLead, refetch]);

  // Sequence editing
  const startEditingSequences = useCallback(() => {
    setSequenceDrafts(
      sequences.map(s => ({
        subject: s.subject,
        body: s.body,
        delay_days: s.delayDays,
        step_number: s.order,
      })),
    );
    setEditingSequences(true);
  }, [sequences]);

  const handleSequenceDraftChange = useCallback((index: number, field: 'subject' | 'body', value: string) => {
    setSequenceDrafts(prev => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  }, []);

  // Insert shortcode at cursor position in the last focused field
  const insertAtCursor = useCallback((shortcodeKey: string, setter: (updater: (prev: string) => string) => void) => {
    const tag = `{{${shortcodeKey}}}`;
    const el = lastFocusedRef.current;
    if (el) {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? start;
      const before = el.value.slice(0, start);
      const after = el.value.slice(end);
      const newValue = before + tag + after;
      // Trigger the setter with the new value
      setter(() => newValue);
      // Restore cursor position after React re-renders
      requestAnimationFrame(() => {
        el.focus();
        const newPos = start + tag.length;
        el.setSelectionRange(newPos, newPos);
      });
    } else {
      // Fallback: append to end
      setter(prev => prev + tag);
    }
  }, []);

  const insertShortcodeInDraft = useCallback(
    (index: number, field: 'subject' | 'body', shortcodeKey: string) => {
      insertAtCursor(shortcodeKey, updater => {
        setSequenceDrafts(prev =>
          prev.map((s, i) => {
            if (i !== index) return s;
            const newVal = typeof updater === 'function' ? updater(s[field]) : updater;
            return { ...s, [field]: newVal };
          }),
        );
      });
    },
    [insertAtCursor],
  );

  const insertShortcodeInLeadSeq = useCallback(
    (leadId: string, seqIdx: number, field: 'subject' | 'body', shortcodeKey: string) => {
      insertAtCursor(shortcodeKey, updater => {
        setLeadSeqEdits(prev => {
          const leadEdits = { ...prev[leadId] };
          const current = leadEdits[seqIdx] || {
            subject: sequences[seqIdx]?.subject || '',
            body: sequences[seqIdx]?.body || '',
          };
          const newVal = typeof updater === 'function' ? updater(current[field]) : updater;
          leadEdits[seqIdx] = { ...current, [field]: newVal };
          return { ...prev, [leadId]: leadEdits };
        });
      });
    },
    [insertAtCursor, sequences],
  );

  // Per-lead sequence editing
  const startEditingLeadSeq = useCallback(
    (leadId: string) => {
      // Initialize with current sequence templates
      const edits: Record<number, { subject: string; body: string }> = {};
      sequences.forEach((seq, idx) => {
        edits[idx] = { subject: seq.subject, body: seq.body };
      });
      setLeadSeqEdits(prev => ({ ...prev, [leadId]: edits }));
      setEditingLeadSeq(leadId);
    },
    [sequences],
  );

  const handleLeadSeqChange = useCallback(
    (leadId: string, seqIdx: number, field: 'subject' | 'body', value: string) => {
      setLeadSeqEdits(prev => {
        const leadEdits = { ...prev[leadId] };
        const current = leadEdits[seqIdx] || { subject: '', body: '' };
        leadEdits[seqIdx] = { ...current, [field]: value };
        return { ...prev, [leadId]: leadEdits };
      });
    },
    [],
  );

  const saveLeadSeqEdits = useCallback(
    async (leadId: string) => {
      // Save as the global sequences (since per-lead overrides aren't supported on backend yet)
      const edits = leadSeqEdits[leadId];
      if (!edits) return;
      const newSequences = sequences.map((seq, idx) => ({
        step_number: seq.order,
        delay_days: seq.delayDays,
        subject: edits[idx]?.subject ?? seq.subject,
        body: edits[idx]?.body ?? seq.body,
      }));
      try {
        await updateSequences({ campaignId, sequences: newSequences }).unwrap();
        setEditingLeadSeq(null);
        refetch();
      } catch {
        // Error handled by RTK Query
      }
    },
    [leadSeqEdits, sequences, campaignId, updateSequences, refetch],
  );

  const saveSequences = useCallback(async () => {
    try {
      await updateSequences({ campaignId, sequences: sequenceDrafts }).unwrap();
      setEditingSequences(false);
      refetch();
    } catch {
      // Error handled by RTK Query
    }
  }, [sequenceDrafts, campaignId, updateSequences, refetch]);

  // Authorize sending
  const handleAuthorize = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      const result = await authorizeSending({
        campaignId,
        leadIds: Array.from(selectedIds),
      }).unwrap();
      setScheduledCount(result.scheduledCount);
      setSendingComplete(true);
    } catch {
      // Error handled by RTK Query
    }
  }, [selectedIds, campaignId, authorizeSending]);

  if (isLoading) {
    return (
      <Panel>
        <EmptyState>Loading email preview...</EmptyState>
      </Panel>
    );
  }

  if (!sequences.length) {
    return null;
  }

  return (
    <Panel>
      <PanelHeader $collapsed={panelCollapsed} onClick={() => setPanelCollapsed(prev => !prev)}>
        <HeaderTop>
          <div>
            <HeaderTitle>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              Review Leads Before Sending
            </HeaderTitle>
            {!panelCollapsed && (
              <HeaderDesc>
                Preview how each email will look per lead. Edit sequences or fix missing fields, then authorize sending.
              </HeaderDesc>
            )}
          </div>
          <ActionBar>
            <CollapseToggle
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setPanelCollapsed(prev => !prev);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: panelCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
              {panelCollapsed ? 'Expand' : 'Collapse'}
            </CollapseToggle>
            {!panelCollapsed && !sendingComplete && (
              <>
                <SelectAllBtn
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    (selectedIds.size > 0 ? deselectAll : selectAll)();
                  }}
                >
                  {selectedIds.size > 0 ? `Deselect All (${selectedIds.size})` : `Select All Valid (${leadsReady})`}
                </SelectAllBtn>
                <AuthBtn
                  disabled={selectedIds.size === 0 || isAuthorizing}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleAuthorize();
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  {isAuthorizing ? 'Authorizing...' : `Authorize Sending (${selectedIds.size})`}
                </AuthBtn>
              </>
            )}
          </ActionBar>
        </HeaderTop>

        <StatsRow>
          <Stat $variant="cyan">
            <strong>{total}</strong>
            <span>Total Leads</span>
          </Stat>
          <Stat $variant="green">
            <strong>{leadsReady}</strong>
            <span>Ready</span>
          </Stat>
          {leadsWithIssues > 0 && (
            <Stat $variant="amber">
              <strong>{leadsWithIssues}</strong>
              <span>Missing Fields</span>
            </Stat>
          )}
          {leadsNoEmail > 0 && (
            <Stat $variant="red">
              <strong>{leadsNoEmail}</strong>
              <span>No Email</span>
            </Stat>
          )}
          <Stat>
            <strong>{sequences.length}</strong>
            <span>{sequences.length === 1 ? 'Email' : 'Email Sequence'}</span>
          </Stat>
        </StatsRow>
      </PanelHeader>

      {!panelCollapsed && (
        <>
          {/* Sequence Editor */}
          <SequenceEditSection>
            <SequenceEditHeader>
              <div
                style={{
                  fontSize: 'var(--sk-text-xs)',
                  fontWeight: 600,
                  color: 'var(--sk-text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Email Sequences ({sequences.length} {sequences.length === 1 ? 'step' : 'steps'})
              </div>
              {!sendingComplete && (
                <SelectAllBtn onClick={editingSequences ? () => setEditingSequences(false) : startEditingSequences}>
                  {editingSequences ? 'Cancel Editing' : 'Edit Sequences'}
                </SelectAllBtn>
              )}
            </SequenceEditHeader>

            {editingSequences ? (
              <div>
                {sequenceDrafts.map((draft, idx) => (
                  <SequenceCard key={`draft-${draft.step_number}`}>
                    <SequenceLabel>
                      Email {idx + 1}
                      {draft.delay_days > 0 && (
                        <span style={{ fontWeight: 400, textTransform: 'none' }}>
                          — {draft.delay_days} day{draft.delay_days !== 1 ? 's' : ''} after previous
                        </span>
                      )}
                      {idx === 0 && <span style={{ fontWeight: 400, textTransform: 'none' }}>— sent immediately</span>}
                    </SequenceLabel>
                    <EditField>
                      <EditLabel>Subject</EditLabel>
                      <EditInput
                        value={draft.subject}
                        onChange={e => handleSequenceDraftChange(idx, 'subject', e.target.value)}
                        onFocus={e => {
                          lastFocusedRef.current = e.target;
                        }}
                        placeholder="Email subject..."
                      />
                    </EditField>
                    <div style={{ marginBottom: '8px' }}>
                      <EditLabel style={{ display: 'block', marginBottom: '4px' }}>Body</EditLabel>
                      <EditTextarea
                        value={draft.body}
                        onChange={e => handleSequenceDraftChange(idx, 'body', e.target.value)}
                        onFocus={e => {
                          lastFocusedRef.current = e.target;
                        }}
                        placeholder="Email body..."
                      />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--sk-text-tertiary)', lineHeight: '24px' }}>
                        Insert at cursor:
                      </span>
                      {AVAILABLE_SHORTCODES.map(sc => (
                        <ShortcodeChip
                          key={sc.key}
                          onClick={() =>
                            insertShortcodeInDraft(
                              idx,
                              lastFocusedRef.current?.tagName === 'INPUT' ? 'subject' : 'body',
                              sc.key,
                            )
                          }
                          title={`Insert {{${sc.key}}} at cursor — resolves to lead's ${sc.label}`}
                        >
                          {`{{${sc.key}}}`}
                        </ShortcodeChip>
                      ))}
                    </div>
                  </SequenceCard>
                ))}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <SaveBtn onClick={saveSequences} style={{ padding: '8px 20px' }}>
                    {isSavingSequences ? 'Saving...' : 'Save Sequences'}
                  </SaveBtn>
                  <SelectAllBtn onClick={() => setEditingSequences(false)}>Cancel</SelectAllBtn>
                </div>
              </div>
            ) : (
              <div>
                {sequences.map((seq, idx) => (
                  <SequenceCard key={seq.id}>
                    <SequenceLabel>
                      Email {idx + 1}
                      {seq.delayDays > 0 && (
                        <span style={{ fontWeight: 400, textTransform: 'none' }}>
                          — {seq.delayDays} day{seq.delayDays !== 1 ? 's' : ''} after previous
                        </span>
                      )}
                      {idx === 0 && <span style={{ fontWeight: 400, textTransform: 'none' }}>— sent immediately</span>}
                    </SequenceLabel>
                    <SequenceSubject>Subject: {seq.subject}</SequenceSubject>
                    <SequenceBody>{stripHtml(seq.body)}</SequenceBody>
                  </SequenceCard>
                ))}
              </div>
            )}
          </SequenceEditSection>

          {sendingComplete && (
            <SuccessBanner>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              {scheduledCount} emails scheduled for delivery. They will be sent according to your campaign schedule.
            </SuccessBanner>
          )}

          {leads.length === 0 ? (
            <EmptyState>No leads with email addresses found for this campaign.</EmptyState>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: 40 }} />
                      <Th>Lead</Th>
                      <Th>Email</Th>
                      <Th>Subject Preview</Th>
                      <Th>Body Preview</Th>
                      <Th style={{ width: 90 }}>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadPreviews.map(lp => (
                      <>
                        <Tr
                          key={lp.lead.id}
                          $selected={selectedIds.has(lp.lead.id)}
                          $hasIssues={lp.hasIssues}
                          onClick={() => toggleExpand(lp.lead.id)}
                        >
                          <Td onClick={e => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(lp.lead.id)}
                              onChange={() => toggleSelect(lp.lead.id)}
                              disabled={!lp.hasValidEmail || sendingComplete}
                            />
                          </Td>
                          <Td>
                            <div style={{ fontWeight: 600 }}>
                              {lp.lead.firstName || lp.lead.company || lp.lead.email?.split('@')[0]}
                              {lp.lead.lastName ? ` ${lp.lead.lastName}` : ''}
                            </div>
                            {lp.lead.company && (
                              <div style={{ fontSize: '12px', color: 'var(--sk-text-tertiary)' }}>
                                {lp.lead.company}
                              </div>
                            )}
                          </Td>
                          <Td>
                            <span style={{ color: 'var(--sk-cyan-500)', fontSize: '13px' }}>
                              {lp.lead.email || <span style={{ color: 'var(--sk-text-tertiary)' }}>No email</span>}
                            </span>
                          </Td>
                          <Td>{renderWithHighlights(lp.subjectPreview)}</Td>
                          <Td>
                            <span style={{ color: 'var(--sk-text-secondary)', fontSize: '12px' }}>
                              {renderWithHighlights(lp.bodyPreview)}
                              {lp.bodyPreview.length >= 120 && '...'}
                            </span>
                          </Td>
                          <Td>
                            {!lp.hasValidEmail ? (
                              <IssuesBadge>No email</IssuesBadge>
                            ) : lp.hasIssues ? (
                              <OkBadge style={{ background: 'rgba(255, 152, 0, 0.1)', color: '#ff9800' }}>
                                {lp.unresolved.length} warn
                              </OkBadge>
                            ) : (
                              <OkBadge>Ready</OkBadge>
                            )}
                          </Td>
                        </Tr>

                        {expandedId === lp.lead.id && (
                          <ExpandedRow key={`${lp.lead.id}-expanded`}>
                            <td colSpan={6}>
                              <ExpandedContent>
                                {/* Inline edit section */}
                                {lp.hasIssues && !sendingComplete && (
                                  <SequenceCard
                                    style={{ borderColor: 'rgba(255, 152, 0, 0.3)', marginBottom: 'var(--sk-space-4)' }}
                                  >
                                    <SequenceLabel>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#ff9800"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        width="14"
                                        height="14"
                                      >
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                      Fix Missing Fields (or edit sequences to use different shortcodes)
                                    </SequenceLabel>
                                    {editingLead && editingLead.id === lp.lead.id ? (
                                      <>
                                        {lp.unresolved.map(field => {
                                          const fieldKey = field.toLowerCase().includes('first')
                                            ? 'firstName'
                                            : field.toLowerCase().includes('last')
                                              ? 'lastName'
                                              : field.toLowerCase().includes('company')
                                                ? 'company'
                                                : field.toLowerCase().includes('city')
                                                  ? 'city'
                                                  : field.toLowerCase().includes('state')
                                                    ? 'state'
                                                    : field.toLowerCase().includes('industry')
                                                      ? 'industry'
                                                      : field;
                                          return (
                                            <EditField key={field}>
                                              <EditLabel>{field}</EditLabel>
                                              <EditInput
                                                value={editingLead[fieldKey] || ''}
                                                onChange={e => handleEditChange(fieldKey, e.target.value)}
                                                placeholder={`Enter ${field}...`}
                                              />
                                            </EditField>
                                          );
                                        })}
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                          <SaveBtn onClick={saveEdit}>Save Changes</SaveBtn>
                                          <SelectAllBtn onClick={() => setEditingLead(null)}>Cancel</SelectAllBtn>
                                        </div>
                                      </>
                                    ) : (
                                      <SelectAllBtn onClick={() => startEditing(lp.lead)}>Edit Lead Data</SelectAllBtn>
                                    )}
                                  </SequenceCard>
                                )}

                                {/* Per-lead email sequences — editable */}
                                {!sendingComplete && editingLeadSeq !== lp.lead.id && (
                                  <div style={{ marginBottom: 'var(--sk-space-3)' }}>
                                    <SelectAllBtn onClick={() => startEditingLeadSeq(lp.lead.id)}>
                                      Edit Sequences for This Lead
                                    </SelectAllBtn>
                                  </div>
                                )}

                                {editingLeadSeq === lp.lead.id ? (
                                  <>
                                    {sequences.map((seq, idx) => {
                                      const edits = leadSeqEdits[lp.lead.id]?.[idx] || {
                                        subject: seq.subject,
                                        body: seq.body,
                                      };
                                      const subjPreview = replaceShortcodes(edits.subject, lp.lead);
                                      const bodyPreview = replaceShortcodes(edits.body, lp.lead);
                                      return (
                                        <SequenceCard key={seq.id}>
                                          <SequenceLabel>
                                            Email {idx + 1}
                                            {seq.delayDays > 0 && (
                                              <span style={{ fontWeight: 400, textTransform: 'none' }}>
                                                — {seq.delayDays} day{seq.delayDays !== 1 ? 's' : ''} after previous
                                              </span>
                                            )}
                                            {idx === 0 && (
                                              <span style={{ fontWeight: 400, textTransform: 'none' }}>
                                                — sent immediately
                                              </span>
                                            )}
                                          </SequenceLabel>
                                          <EditField>
                                            <EditLabel>Subject</EditLabel>
                                            <EditInput
                                              value={edits.subject}
                                              onChange={e =>
                                                handleLeadSeqChange(lp.lead.id, idx, 'subject', e.target.value)
                                              }
                                              onFocus={e => {
                                                lastFocusedRef.current = e.target;
                                              }}
                                            />
                                          </EditField>
                                          <div style={{ marginBottom: '6px' }}>
                                            <EditLabel style={{ display: 'block', marginBottom: '4px' }}>
                                              Body
                                            </EditLabel>
                                            <EditTextarea
                                              value={edits.body}
                                              onChange={e =>
                                                handleLeadSeqChange(lp.lead.id, idx, 'body', e.target.value)
                                              }
                                              onFocus={e => {
                                                lastFocusedRef.current = e.target;
                                              }}
                                            />
                                          </div>
                                          <div
                                            style={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: '4px',
                                              marginBottom: '8px',
                                            }}
                                          >
                                            <span
                                              style={{
                                                fontSize: '11px',
                                                color: 'var(--sk-text-tertiary)',
                                                lineHeight: '24px',
                                              }}
                                            >
                                              Insert at cursor:
                                            </span>
                                            {AVAILABLE_SHORTCODES.map(sc => (
                                              <ShortcodeChip
                                                key={sc.key}
                                                onClick={() =>
                                                  insertShortcodeInLeadSeq(
                                                    lp.lead.id,
                                                    idx,
                                                    lastFocusedRef.current?.tagName === 'INPUT' ? 'subject' : 'body',
                                                    sc.key,
                                                  )
                                                }
                                                title={`Insert {{${sc.key}}} at cursor`}
                                              >
                                                {`{{${sc.key}}}`}
                                              </ShortcodeChip>
                                            ))}
                                          </div>
                                          <div
                                            style={{
                                              fontSize: '11px',
                                              color: 'var(--sk-text-tertiary)',
                                              marginTop: '4px',
                                              padding: '6px 8px',
                                              background: 'var(--sk-bg-secondary)',
                                              borderRadius: 'var(--sk-radius-sm)',
                                            }}
                                          >
                                            Preview:{' '}
                                            <strong style={{ color: 'var(--sk-text-primary)' }}>
                                              {subjPreview.text}
                                            </strong>
                                            <div
                                              style={{
                                                color: 'var(--sk-text-secondary)',
                                                marginTop: '2px',
                                                whiteSpace: 'pre-wrap',
                                              }}
                                            >
                                              {stripHtml(bodyPreview.text).slice(0, 200)}
                                              {stripHtml(bodyPreview.text).length > 200 && '...'}
                                            </div>
                                          </div>
                                        </SequenceCard>
                                      );
                                    })}
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                      <SaveBtn onClick={() => saveLeadSeqEdits(lp.lead.id)}>
                                        Save Sequence Changes
                                      </SaveBtn>
                                      <SelectAllBtn onClick={() => setEditingLeadSeq(null)}>Cancel</SelectAllBtn>
                                    </div>
                                  </>
                                ) : (
                                  sequences.map((seq, idx) => {
                                    const subjResult = replaceShortcodes(seq.subject, lp.lead);
                                    const bodyResult = replaceShortcodes(seq.body, lp.lead);
                                    return (
                                      <SequenceCard key={seq.id}>
                                        <SequenceLabel>
                                          Email {idx + 1}
                                          {seq.delayDays > 0 && (
                                            <span style={{ fontWeight: 400, textTransform: 'none' }}>
                                              — sent {seq.delayDays} day{seq.delayDays !== 1 ? 's' : ''} after previous
                                            </span>
                                          )}
                                          {idx === 0 && (
                                            <span style={{ fontWeight: 400, textTransform: 'none' }}>
                                              — sent immediately
                                            </span>
                                          )}
                                        </SequenceLabel>
                                        <SequenceSubject>
                                          Subject: {renderWithHighlights(subjResult.text)}
                                        </SequenceSubject>
                                        <SequenceBody>{renderWithHighlights(stripHtml(bodyResult.text))}</SequenceBody>
                                      </SequenceCard>
                                    );
                                  })
                                )}
                              </ExpandedContent>
                            </td>
                          </ExpandedRow>
                        )}
                      </>
                    ))}
                  </tbody>
                </Table>
              </div>

              {totalPages > 1 && (
                <Pagination>
                  <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                    Previous
                  </PageBtn>
                  <span style={{ color: 'var(--sk-text-secondary)', fontSize: 'var(--sk-text-sm)' }}>
                    Page {page} of {totalPages}
                  </span>
                  <PageBtn disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    Next
                  </PageBtn>
                </Pagination>
              )}
            </>
          )}
        </>
      )}
    </Panel>
  );
};

export default memo(EmailReviewPanel);
